import React, { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { User } from 'lucide-react';
import { useRef } from 'react';

const Confetti = ({ delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 0, x: 0, rotate: 0 }}
      animate={{
        opacity: [0, 1, 1, 0],
        y: [-20, -60],
        x: [0, Math.random() * 40 - 20],
        rotate: [0, 360]
      }}
      transition={{
        duration: 1.5,
        delay: delay,
        ease: "easeOut"
      }}
      className="absolute"
      style={{
        left: `${Math.random() * 100}%`,
        top: '50%'
      }}
    >
      <div
        className="w-2 h-2 rounded-full"
        style={{ backgroundColor: ['#11830f', '#F2F0E9', '#11830f'][Math.floor(Math.random() * 3)] }}
      />
    </motion.div>
  );
};

const Counter = ({ end }) => {
  const [count, setCount] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    
    let start = 0;
    const duration = 2000;
    const increment = end / (duration / 16);
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        setShowConfetti(true);
        clearInterval(timer);
        setTimeout(() => setShowConfetti(false), 2000);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [end, isInView]);

  return (
    <div ref={ref} className="relative inline-block">
      <span className="text-2xl font-bold text-[#1A1A1A]">{count}+</span>
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(12)].map((_, i) => (
            <Confetti key={i} delay={i * 0.1} />
          ))}
        </div>
      )}
    </div>
  );
};

const About = () => {
  return (
    <section className="section-spacing bg-white relative overflow-hidden" id="sobre">
      {/* Decorative leaf shapes */}
      <div className="absolute top-0 right-0 w-72 h-72 opacity-[0.04]" style={{
        background: 'radial-gradient(ellipse at center, #11830f 0%, transparent 70%)',
        borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
        transform: 'translate(30%, -30%)'
      }} />
      <div className="absolute bottom-0 left-0 w-96 h-96 opacity-[0.03]" style={{
        background: 'radial-gradient(ellipse at center, #11830f 0%, transparent 70%)',
        borderRadius: '70% 30% 30% 70% / 60% 40% 60% 40%',
        transform: 'translate(-30%, 30%)'
      }} />
      {/* Subtle botanical line art */}
      <svg className="absolute top-12 right-12 w-48 h-48 opacity-[0.04] text-brand-green" viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="1">
        <path d="M100 180 C100 180 100 100 100 60 C100 30 130 10 160 30 C130 30 110 50 100 80" />
        <path d="M100 120 C100 120 70 90 50 80 C30 70 20 40 50 30 C40 50 60 70 100 100" />
        <path d="M100 150 C100 150 130 130 150 120 C170 110 190 120 180 150 C160 130 140 130 100 150" />
      </svg>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Texto - Esquerda */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6 order-2 lg:order-1"
          >
            <div className="space-y-2">
              <p className="text-lg text-gray-500">Oi! Eu sou o</p>
              <p className="text-3xl md:text-4xl text-gray-400 font-light">Nutricionista</p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-brand-green leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Thiago Ferreira
              </h2>
            </div>

            <div className="w-16 h-1 bg-brand-green"></div>

            <p className="text-lg leading-relaxed text-gray-600" style={{ textAlign: 'justify' }}>
              Formado pela Universidade Federal de Lavras (UFLA), meu diferencial está no cuidado e na profundidade do atendimento. Realizo uma anamnese detalhada para compreender verdadeiramente a rotina, os hábitos e as particularidades de cada paciente. A partir disso, elaboro estratégias nutricionais personalizadas que não se limitam apenas à alimentação ideal no papel, mas que sejam realmente possíveis de seguir no dia a dia, sempre respeitando os objetivos e a realidade de cada pessoa.
            </p>

            <div className="pt-4 flex justify-center">
              <div className="text-center bg-[#F2F0E9] rounded-2xl p-6 max-w-[200px]" data-testid="about-stat-1">
                <User className="w-12 h-12 text-brand-green mx-auto mb-3" strokeWidth={1.5} />
                <Counter end={200} />
                <p className="text-sm text-gray-600 mt-2">Pacientes<br/>atendidos</p>
              </div>
            </div>
          </motion.div>

          {/* Imagem - Direita */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-1 lg:order-2 flex justify-center lg:justify-end"
          >
            <div className="relative w-full max-w-md lg:max-w-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_site-generator-134/artifacts/gxindvpv_WhatsApp%20Image%202026-02-07%20at%2011.13.34%20%281%29.jpeg"
                alt="Thiago Ferreira - Nutricionista"
                className="w-full h-auto rounded-[40%] object-cover shadow-2xl"
                style={{ aspectRatio: '3/4' }}
                data-testid="about-image"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
